import { useEffect, useState, useMemo, useRef, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

export interface CommandItem {
  id: string;
  title: string;
  category: "Floor & Service" | "Inventory & Spirits" | "Guests & C33" | "Publishing & Growth" | "Intelligence & Admin";
  route: string;
  keywords: string[];
  icon: string;
}

export const ALL_COMMANDS: CommandItem[] = [
  // 🍸 Floor & Service
  { id: "pick-list", title: "Floor Pick List", category: "Floor & Service", route: "/admin/pick-list", keywords: ["restock", "bottles", "walking path", "bar", "floor", "pull"], icon: "📋" },
  { id: "operations", title: "Shift Checklists", category: "Floor & Service", route: "/admin/operations", keywords: ["opening", "closing", "checklists", "duties", "tasks", "ops"], icon: "✅" },
  { id: "cocktail-mapping", title: "Cocktail Pour Mapping", category: "Floor & Service", route: "/admin/cocktail-mapping", keywords: ["square", "pos", "recipes", "pours", "deductions"], icon: "🍸" },
  { id: "recipes", title: "Cocktail Recipes", category: "Floor & Service", route: "/admin/recipes", keywords: ["drinks", "cocktails", "specs", "build", "glassware"], icon: "🍹" },
  { id: "prep-recipes", title: "Prep Recipes & Syrups", category: "Floor & Service", route: "/admin/prep-recipes", keywords: ["batch", "syrups", "infusions", "juices", "kitchen"], icon: "🍯" },
  { id: "ingredients", title: "Bar Ingredients", category: "Floor & Service", route: "/admin/ingredients", keywords: ["garnishes", "bitters", "produce", "mixers"], icon: "🍋" },
  { id: "cash", title: "Cash Management", category: "Floor & Service", route: "/admin/cash-management", keywords: ["drawer", "bank", "drop", "cash", "safe"], icon: "💵" },
  { id: "shelf-map", title: "Shelf Bottle Mappings", category: "Floor & Service", route: "/admin/shelf-map", keywords: ["shelves", "bays", "slots", "locations", "library"], icon: "🗺️" },
  { id: "tasting-mat", title: "Tasting Mats", category: "Floor & Service", route: "/admin/tasting-mat", keywords: ["flights", "print", "placemat", "tasting sheet"], icon: "📜" },
  { id: "flights", title: "Flight Builder", category: "Floor & Service", route: "/admin/flights", keywords: ["whiskey flights", "tasting flights", "combos"], icon: "🥃" },

  // 📦 Inventory & Spirits
  { id: "inventory", title: "Inventory Master", category: "Inventory & Spirits", route: "/admin/inventory", keywords: ["bottles", "spirits", "stock", "catalog", "menu items"], icon: "📦" },
  { id: "inventory-count", title: "Inventory Cycle Count", category: "Inventory & Spirits", route: "/admin/inventory-count", keywords: ["audit", "variance", "physical count", "dead stock", "velocity"], icon: "🔢" },
  { id: "whiskey-intel", title: "Whiskey Intelligence & Allocations", category: "Inventory & Spirits", route: "/admin/whiskey-intel", keywords: ["distributors", "reps", "allocations", "rare barrels", "touchpoints"], icon: "🕵️" },
  { id: "enrichment", title: "Catalog & Distillery Enrichment", category: "Inventory & Spirits", route: "/admin/enrichment", keywords: ["mashbill", "abv", "age", "distillery provenance", "ai specs"], icon: "✨" },
  { id: "catalog-monitor", title: "Catalog Drift Monitor", category: "Inventory & Spirits", route: "/admin/catalog-monitor", keywords: ["deleted", "drift", "sync", "audit"], icon: "📡" },
  { id: "merge", title: "Merge Duplicate Bottles", category: "Inventory & Spirits", route: "/admin/merge", keywords: ["duplicates", "dedupe", "merge items"], icon: "🔀" },
  { id: "curation", title: "Curation & Media", category: "Inventory & Spirits", route: "/admin/curation", keywords: ["bottle photography", "labels", "hero images"], icon: "🎨" },

  // 👥 Guests & C33
  { id: "communications", title: "Unified Inbox & Softphone", category: "Guests & C33", route: "/admin/communications", keywords: ["sms", "messages", "phone calls", "inbox", "twilio"], icon: "💬" },
  { id: "crm", title: "Guest CRM & Contacts", category: "Guests & C33", route: "/admin/crm", keywords: ["customers", "profiles", "vip", "history", "contacts"], icon: "👥" },
  { id: "members", title: "C33 Member Roster", category: "Guests & C33", route: "/admin/members", keywords: ["collective 33", "membership", "lockers", "tier"], icon: "👑" },
  { id: "club-pours", title: "Club Pour Planner", category: "Guests & C33", route: "/admin/club-pours", keywords: ["monthly pours", "c33 bottle", "allocations"], icon: "🍷" },
  { id: "events", title: "Events & Schedule", category: "Guests & C33", route: "/admin/events", keywords: ["master class", "tasting events", "reservations", "attendees"], icon: "📅" },
  { id: "reviews", title: "Reviews (Guest & Google)", category: "Guests & C33", route: "/admin/reviews", keywords: ["google reviews", "moderation", "ratings", "replies", "ai response"], icon: "⭐" },
  { id: "bottle-photos", title: "Member Bottle Photos", category: "Guests & C33", route: "/admin/bottle-photos", keywords: ["photo queue", "gallery", "member uploads"], icon: "📸" },
  { id: "trails", title: "Whiskey Trails & Passports", category: "Guests & C33", route: "/admin/trails", keywords: ["tasting passports", "stamps", "routes", "distilleries"], icon: "🧭" },
  { id: "suggested-edits", title: "Community Suggested Edits", category: "Guests & C33", route: "/admin/suggested-edits", keywords: ["corrections", "crowdsource", "mashbill fix"], icon: "✍️" },

  // 📢 Publishing & Growth
  { id: "articles", title: "Editorial Articles & News", category: "Publishing & Growth", route: "/admin/articles", keywords: ["journal", "news", "dispatches", "blog", "posts"], icon: "📰" },
  { id: "templates", title: "Email & SMS Templates", category: "Publishing & Growth", route: "/admin/templates", keywords: ["marketing", "notifications", "merge tags", "lifecycle"], icon: "✉️" },
  { id: "partners", title: "Brand Partners & Sponsors", category: "Publishing & Growth", route: "/admin/partners", keywords: ["distilleries", "sponsors", "logos", "socials"], icon: "🤝" },
  { id: "seo", title: "Search & Ambassador Referrals", category: "Publishing & Growth", route: "/admin/seo", keywords: ["google search console", "keywords", "referrals", "analytics"], icon: "📈" },
  { id: "charity", title: "S3 Giving & Charity Ledger", category: "Publishing & Growth", route: "/admin/charity", keywords: ["donations", "non-profit", "community giving"], icon: "❤️" },

  // ⚙️ Intelligence & Admin
  { id: "dashboard", title: "KPI Executive Dashboard", category: "Intelligence & Admin", route: "/admin/dashboard", keywords: ["kpi", "sales", "depletions", "metrics", "overview"], icon: "📊" },
  { id: "business-context", title: "Business Context & Demand", category: "Intelligence & Admin", route: "/admin/business-context", keywords: ["chiefs", "weather", "concerts", "conventions", "demand"], icon: "🌤️" },
  { id: "knowledge", title: "Knowledge Base & Staff SOPs", category: "Intelligence & Admin", route: "/admin/knowledge", keywords: ["ask sentinel", "sops", "procedures", "training", "manual"], icon: "🧠" },
  { id: "activity", title: "Audit Activity Log", category: "Intelligence & Admin", route: "/admin/activity", keywords: ["audit log", "changes", "staff history"], icon: "🕒" },
  { id: "comp-audit", title: "Comp & Waste Audit", category: "Intelligence & Admin", route: "/admin/comp-audit", keywords: ["comps", "spills", "shrinkage", "discounts"], icon: "🔍" },
  { id: "issues", title: "Issue Reports", category: "Intelligence & Admin", route: "/admin/issues", keywords: ["bugs", "maintenance", "trouble tickets"], icon: "⚠️" },
  { id: "voice", title: "Voice Bot Settings", category: "Intelligence & Admin", route: "/admin/settings/voice", keywords: ["ai voice", "phone bot", "twilio voice", "prompts"], icon: "🎙️" },
  { id: "import-export", title: "Import / Export Data", category: "Intelligence & Admin", route: "/admin/import-export", keywords: ["csv", "json", "backup", "data sync"], icon: "💾" },
  { id: "settings", title: "General Settings", category: "Intelligence & Admin", route: "/admin/settings", keywords: ["configuration", "hours", "profile"], icon: "⚙️" },
  { id: "staff-access", title: "Staff Access & Permissions", category: "Intelligence & Admin", route: "/admin/staff-access", keywords: ["users", "roles", "rbac", "passcodes", "owners"], icon: "🔒" },
  { id: "station-tablets", title: "Station Floor Tablets", category: "Intelligence & Admin", route: "/admin/station-tablets", keywords: ["ipads", "tablets", "kiosk", "stations"], icon: "📱" },
];

const CATEGORY_COLORS: Record<string, string> = {
  "Floor & Service": "#27ae60",
  "Inventory & Spirits": "#F9AF36",
  "Guests & C33": "#9B59B6",
  "Publishing & Growth": "#4A8FD9",
  "Intelligence & Admin": "#E67E22",
};

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const filteredCommands = useMemo(() => {
    let list = ALL_COMMANDS;
    if (activeCategory !== "All") {
      list = list.filter((c) => c.category === activeCategory);
    }
    if (!query.trim()) return list;

    const q = query.toLowerCase().trim();
    return list.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q) ||
        c.keywords.some((k) => k.toLowerCase().includes(q))
    );
  }, [query, activeCategory]);

  // Keep selected index within bounds
  useEffect(() => {
    if (selectedIndex >= filteredCommands.length) {
      setSelectedIndex(0);
    }
  }, [filteredCommands, selectedIndex]);

  const handleSelect = (item: CommandItem) => {
    onClose();
    navigate(item.route);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredCommands.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % Math.max(1, filteredCommands.length));
    } else if (e.key === "Enter" && filteredCommands[selectedIndex]) {
      e.preventDefault();
      handleSelect(filteredCommands[selectedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        {/* Search Header */}
        <div style={styles.searchHeader}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a tool, bottle, action, or keyword… (↑↓ to navigate, ↵ to open)"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            style={styles.searchInput}
          />
          <span style={styles.escBadge}>ESC</span>
        </div>

        {/* Category Filter Chips */}
        <div style={styles.filterBar}>
          {["All", "Floor & Service", "Inventory & Spirits", "Guests & C33", "Publishing & Growth", "Intelligence & Admin"].map((cat) => (
            <button
              key={cat}
              type="button"
              style={{
                ...styles.filterChip,
                background: activeCategory === cat ? "rgba(249,175,54,0.15)" : "transparent",
                borderColor: activeCategory === cat ? "var(--gold)" : "var(--border)",
                color: activeCategory === cat ? "var(--gold)" : "var(--muted)",
              }}
              onClick={() => {
                setActiveCategory(cat);
                setSelectedIndex(0);
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results List */}
        <div style={styles.resultsList}>
          {filteredCommands.length === 0 ? (
            <div style={styles.emptyState}>No matching tools or actions found.</div>
          ) : (
            filteredCommands.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              const catColor = CATEGORY_COLORS[item.category] || "var(--gold)";
              return (
                <div
                  key={item.id}
                  style={{
                    ...styles.itemRow,
                    background: isSelected ? "var(--surface)" : "transparent",
                    borderColor: isSelected ? "var(--gold)" : "transparent",
                  }}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span style={styles.itemIcon}>{item.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={styles.itemTitle}>{item.title}</div>
                    <div style={styles.itemKeywords}>
                      {item.keywords.slice(0, 4).join(" · ")}
                    </div>
                  </div>
                  <span
                    style={{
                      ...styles.itemCategory,
                      color: catColor,
                      background: `${catColor}18`,
                    }}
                  >
                    {item.category}
                  </span>
                  {isSelected && <span style={styles.enterKey}>↵ Open</span>}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span><strong>Navigation Tip:</strong> Press <kbd style={styles.kbd}>⌘K</kbd> or <kbd style={styles.kbd}>Ctrl+K</kbd> from anywhere in Station.</span>
          <span>{filteredCommands.length} tools available</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "10vh",
    zIndex: 9999,
  },
  modal: {
    background: "var(--bg, #0E0C0A)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 14,
    width: "100%",
    maxWidth: 680,
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(249, 175, 54, 0.15)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  searchHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 20px",
    borderBottom: "1px solid var(--border, #2A1F14)",
    background: "rgba(255,255,255,0.02)",
  },
  searchIcon: {
    fontSize: 18,
    opacity: 0.6,
  },
  searchInput: {
    flex: 1,
    background: "none",
    border: "none",
    outline: "none",
    color: "var(--text, #E6DEC8)",
    fontSize: 15,
    fontFamily: "inherit",
  },
  escBadge: {
    fontSize: 10,
    fontWeight: 700,
    background: "var(--surface, #1A140F)",
    border: "1px solid var(--border, #2A1F14)",
    padding: "3px 6px",
    borderRadius: 4,
    color: "var(--muted, #8A7A66)",
  },
  filterBar: {
    display: "flex",
    gap: 6,
    padding: "8px 16px",
    overflowX: "auto",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },
  filterChip: {
    padding: "4px 10px",
    borderRadius: 16,
    border: "1px solid",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.15s",
  },
  resultsList: {
    maxHeight: 380,
    overflowY: "auto",
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid transparent",
    cursor: "pointer",
    transition: "background 0.1s, border-color 0.1s",
  },
  itemIcon: {
    fontSize: 18,
    width: 24,
    textAlign: "center",
  },
  itemTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text, #E6DEC8)",
  },
  itemKeywords: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  itemCategory: {
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  enterKey: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    marginLeft: 8,
  },
  emptyState: {
    padding: "36px 20px",
    textAlign: "center",
    color: "var(--muted, #8A7A66)",
    fontSize: 13,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 18px",
    borderTop: "1px solid var(--border, #2A1F14)",
    background: "rgba(0,0,0,0.2)",
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  kbd: {
    background: "var(--surface, #1A140F)",
    border: "1px solid var(--border, #2A1F14)",
    padding: "1px 4px",
    borderRadius: 3,
    fontSize: 10,
    color: "var(--text, #E6DEC8)",
  },
};
